import type { ChatOptions } from '@chatbot/typings/types';
import { defaultOptions, defaultMountingTarget } from '@chatbot/constants/chat';
import { createDefaultMountingTarget } from '@chatbot/utils/mount';

/**
 * Deep merges chat options with defaults
 */
export const resolveOptions = (options?: Partial<ChatOptions>): ChatOptions => ({
    ...defaultOptions,
    ...options,
    theme: {
        ...defaultOptions.theme,
        ...options?.theme,
    },
    position: {
        ...defaultOptions.position,
        ...options?.position,
    },
    metadata: {
        ...defaultOptions.metadata,
        ...options?.metadata,
    },
});

/**
 * Merges option groups (theme, position, metadata) into reactive options
 */
export const mergeOptionGroups = (newOptions: Partial<ChatOptions>,
    reactiveOptions: ChatOptions): void => {
    const optionGroups = [
        'theme', 'position', 'metadata',
    ] as const;

    optionGroups.forEach(group => {
        if (newOptions[group] && reactiveOptions[group]) {
            Object.assign(reactiveOptions[group] as Record<string, unknown>,
                newOptions[group] as Record<string, unknown>);
        }
    });
};

/**
 * Updates non-grouped options directly
 */
export const updateDirectOptions = (newOptions: Partial<ChatOptions>,
    reactiveOptions: ChatOptions): void => {
    const excludedKeys = [
        'theme', 'position', 'metadata',
    ];

    Object.keys(newOptions)
        .forEach(key => {
            if (!excludedKeys.includes(key)) {
                (reactiveOptions as unknown as Record<string, unknown>)[key] = (newOptions as unknown as Record<string, unknown>)[key];
            }
        });
};

/**
 * Resolves the mounting target element
 */
export const resolveMountingTarget = (resolvedOptions: ChatOptions): HTMLElement => {
    const mountingTarget = resolvedOptions.target ?? defaultMountingTarget;
    return typeof mountingTarget === 'string'
        ? createDefaultMountingTarget(mountingTarget)
        : mountingTarget;
};
